import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { jobs } from '../utils/api';
import { toast } from 'sonner';
import { ArrowLeft, Briefcase, X, Sparkles, Loader2, GripVertical, ChevronUp, ChevronDown } from 'lucide-react';

const SHIFT_OPTIONS = [
    { label: '10am - 5pm (7 hrs)', value: '10am-5pm', hours: 7 },
    { label: '9am - 4pm (7 hrs)', value: '9am-4pm', hours: 7 },
    { label: 'Custom', value: 'custom', hours: null },
];

const WORKING_STYLE_TAGS = [
    'Self-managing', 'Client-facing', 'High-growth environment',
    'Accuracy critical', 'Strategic hire', 'People manager',
    'Highly regulated', 'Deadline-driven', 'Cross-functional',
    'Data-driven', 'Ideas-led', 'Process-led', 'Revenue-focused',
];

const COMMON_SKILLS = [
    'Microsoft Office', 'Communication', 'Data Entry', 'Customer Service',
    'Problem Solving', 'Time Management', 'Teamwork', 'Leadership',
    'Project Management', 'Analytical Skills', 'Excel', 'Presentation Skills',
    'Sales', 'Negotiation', 'CRM', 'Marketing', 'Social Media',
    'Accounting', 'Report Writing', 'Research'
];

const ENVIRONMENT_OPTIONS = [
    "Clear goals and autonomy", "Fast-paced", "Structured and predictable",
    "Collaborative / social", "Deep focus / independent", "Customer-facing", "Mission-driven"
];
const TEAM_OPTIONS = [
    "Small and tight-knit", "Large and specialised", "Mostly independent",
    "Highly collaborative", "Mixed / situational"
];
const MANAGER_OPTIONS = [
    "Clear direction and check-ins", "Trusts me with autonomy", "Coaching-oriented",
    "Direct and outcome-focused", "Hands-off unless needed"
];

const MIN_WAGE = parseFloat(process.env.REACT_APP_MINIMUM_WAGE_HOURLY) || 12.71;

const JobPost = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [sectorRoles, setSectorRoles] = useState({});
    const [selectedSector, setSelectedSector] = useState('');
    const [selectedSkills, setSelectedSkills] = useState([]);
    const [customSkill, setCustomSkill] = useState('');
    const [shiftType, setShiftType] = useState('10am-5pm');
    const [customShiftFrom, setCustomShiftFrom] = useState('');
    const [customShiftTo, setCustomShiftTo] = useState('');
    const [customShiftHours, setCustomShiftHours] = useState('');
    const [payType, setPayType] = useState('minimum'); // 'minimum' or 'custom'
    const [customPayRate, setCustomPayRate] = useState('');
    const [generatingDesc, setGeneratingDesc] = useState(false);
    const [descriptionMode, setDescriptionMode] = useState('ai'); // 'ai' | 'manual'
    const [costEstimate, setCostEstimate] = useState(null);
    const [rankedSkills, setRankedSkills] = useState([]);
    const [workingStylePrefs, setWorkingStylePrefs] = useState({
        environment: [], team_size: [], management_style: []
    });
    const [selectedWorkingStyleTags, setSelectedWorkingStyleTags] = useState([]);
    const [formData, setFormData] = useState({
        title: '', description: '', brief: '', salary: '',
        trial_length_days: '5', location: '', work_arrangement: 'office',
    });

    useEffect(() => {
        loadRoles();
    }, []);

    const loadRoles = async () => {
        try {
            const res = await jobs.getRoles();
            setSectorRoles(res.data.sectors || {});
        } catch (error) {
            toast.error('Failed to load roles');
        }
    };

    // Calculate cost whenever relevant fields change
    useEffect(() => {
        calculateCost();
    }, [formData.trial_length_days, payType, customPayRate, shiftType, customShiftHours]);

    const calculateCost = async () => {
        const days = parseInt(formData.trial_length_days) || 5;
        const rate = payType === 'minimum' ? MIN_WAGE : (parseFloat(customPayRate) || MIN_WAGE);
        let hours = 7;
        if (shiftType === 'custom') {
            hours = parseFloat(customShiftHours) || 7;
        } else {
            const opt = SHIFT_OPTIONS.find(o => o.value === shiftType);
            hours = opt?.hours || 7;
        }

        try {
            const res = await jobs.estimateCost({ days, hourly_rate: rate, hours_per_day: hours });
            setCostEstimate(res.data);
        } catch (error) {
            // Fallback client-side calc
            const platformPerDay = 200;
            const candPerDay = rate * hours;
            const totalPerDay = platformPerDay + candPerDay;
            setCostEstimate({
                days, platform_fee_per_day: platformPerDay, candidate_pay_per_day: candPerDay,
                total_per_day: totalPerDay, hourly_rate: rate, hours_per_day: hours,
                platform_fee_total: platformPerDay * days, candidate_pay_total: candPerDay * days,
                trial_total: totalPerDay * days, conversion_fee: 750, total_if_hired: (totalPerDay * days) + 750,
            });
        }
    };

    const handleGenerateDescription = async () => {
        if (!formData.title) {
            toast.error('Please select a job title first');
            return;
        }
        if (!formData.brief.trim()) {
            toast.error('Please write a brief description of what you are looking for');
            return;
        }
        setGeneratingDesc(true);
        try {
            const res = await jobs.generateDescription({ title: formData.title, brief: formData.brief });
            setFormData(prev => ({ ...prev, description: res.data.description }));
            toast.success('Job description generated!');
        } catch (error) {
            toast.error('Failed to generate description. Try again.');
        } finally {
            setGeneratingDesc(false);
        }
    };

    const handleSkillToggle = (skill) => {
        setSelectedSkills(prev =>
            prev.includes(skill) ? prev.filter(s => s !== skill) : [...prev, skill]
        );
    };

    const handleAddCustomSkill = () => {
        const skill = customSkill.trim();
        if (skill && !selectedSkills.includes(skill)) {
            setSelectedSkills(prev => [...prev, skill]);
            setCustomSkill('');
        }
    };

    const handleAddRankedSkill = (skill) => {
        if (rankedSkills.length >= 5) { toast.error('Maximum 5 ranked skills'); return; }
        if (rankedSkills.find(r => r.skill === skill)) return;
        setRankedSkills(prev => [...prev, { skill, importance: 3 }]);
    };

    const handleRemoveRankedSkill = (skill) => {
        setRankedSkills(prev => prev.filter(r => r.skill !== skill));
    };

    const handleRankChange = (skill, importance) => {
        setRankedSkills(prev => prev.map(r =>
            r.skill === skill ? { ...r, importance: Math.max(1, Math.min(5, importance)) } : r
        ));
    };

    const moveRankedSkill = (index, direction) => {
        const items = [...rankedSkills];
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= items.length) return;
        [items[index], items[newIndex]] = [items[newIndex], items[index]];
        setRankedSkills(items);
    };

    const toggleWorkingStyle = (field, value) => {
        setWorkingStylePrefs(prev => ({
            ...prev,
            [field]: prev[field].includes(value)
                ? prev[field].filter(v => v !== value)
                : [...prev[field], value]
        }));
    };

    const toggleWorkingStyleTag = (tag) => {
        setSelectedWorkingStyleTags(prev =>
            prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
        );
    };

    const getHoursPerDay = () => {
        if (shiftType === 'custom') return parseFloat(customShiftHours) || 7;
        return SHIFT_OPTIONS.find(o => o.value === shiftType)?.hours || 7;
    };

    const getPayRate = () => {
        return payType === 'minimum' ? MIN_WAGE : (parseFloat(customPayRate) || MIN_WAGE);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.title) { toast.error('Please select a role'); return; }
        if (!formData.description) { toast.error('Please generate a job description'); return; }
        if (!formData.location) { toast.error('Please select a location'); return; }

        setLoading(true);
        try {
            const data = {
                title: formData.title,
                description: formData.description,
                sector: selectedSector || 'office',
                skills: selectedSkills,
                required_skills_ranked: rankedSkills,
                working_style_preferences: workingStylePrefs,
                working_style_tags: selectedWorkingStyleTags,
                pay_rate: getPayRate(),
                salary: formData.salary || null,
                trial_length_days: parseInt(formData.trial_length_days),
                location: formData.location,
                hours_per_day: getHoursPerDay(),
                work_arrangement: formData.work_arrangement,
                shift_pattern: shiftType === 'custom' ? `${customShiftFrom} - ${customShiftTo}` : shiftType,
            };
            await jobs.create(data);
            toast.success('Job posted successfully!');
            navigate('/company/dashboard');
        } catch (error) {
            toast.error(error.userMessage || 'Failed to post job');
        } finally {
            setLoading(false);
        }
    };

    const roles = selectedSector ? (sectorRoles[selectedSector] || []) : [];

    return (
        <div className="min-h-screen" style={{ backgroundColor: '#F0FDF4' }}>
            <header className="sticky top-0 z-50 bg-[#F0FDF4]/80 backdrop-blur-xl border-b border-purple-200/50 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
                    <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
                        <img src="/sampler-logo.png" alt="Sampler" className="h-10 w-auto" />
                        <span className="text-2xl font-bold" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>Sampler</span>
                    </div>
                    <Button variant="ghost" className="text-slate-600 hover:text-[#8B5CF6] hover:bg-purple-50" onClick={() => navigate('/company/dashboard')} data-testid="back-btn">
                        <ArrowLeft className="mr-2 h-4 w-4" /> Back
                    </Button>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-4 md:px-8 py-10">
                <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#8B5CF6] block mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                    New Role
                </span>
                <h1 className="text-4xl font-bold mb-1 tracking-tight" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }} data-testid="post-job-heading">Post a Job</h1>
                <p className="mb-8 text-slate-500" style={{ fontFamily: 'Barlow, sans-serif' }}>Create a job posting and find the perfect candidates</p>

                <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-8 border border-purple-200/50 shadow-sm space-y-6">

                    {/* Sector + Role (Cascading Dropdowns) */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <Label className="text-slate-700 font-semibold text-sm">Sector</Label>
                            <Select value={selectedSector} onValueChange={(val) => { setSelectedSector(val); setFormData(prev => ({ ...prev, title: '' })); }}>
                                <SelectTrigger className="mt-1.5 rounded-xl border-purple-200/50 bg-white" data-testid="sector-select">
                                    <SelectValue placeholder="Select sector" />
                                </SelectTrigger>
                                <SelectContent className="bg-white rounded-xl">
                                    {Object.keys(sectorRoles).map(s => (
                                        <SelectItem key={s} value={s}>{s}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-slate-700 font-semibold text-sm">Job Title</Label>
                            <Select value={formData.title} onValueChange={(val) => setFormData(prev => ({ ...prev, title: val }))} disabled={!selectedSector}>
                                <SelectTrigger className="mt-1.5 rounded-xl border-purple-200/50 bg-white" data-testid="title-select">
                                    <SelectValue placeholder={selectedSector ? 'Select role' : 'Select a sector first'} />
                                </SelectTrigger>
                                <SelectContent className="bg-white rounded-xl max-h-64">
                                    {roles.map(r => (
                                        <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Description mode toggle */}
                    <div>
                        <Label className="text-slate-700 font-semibold text-sm">Job Description</Label>
                        <div className="flex gap-2 mt-1.5 mb-3">
                            <button
                                type="button"
                                onClick={() => setDescriptionMode('ai')}
                                data-testid="description-mode-ai-btn"
                                className={`flex-1 rounded-xl border text-sm font-semibold py-2 px-3 transition-colors ${descriptionMode === 'ai' ? 'bg-[#8B5CF6] text-white border-[#8B5CF6]' : 'bg-white text-slate-600 border-purple-200/50 hover:border-[#8B5CF6]'}`}
                            >
                                <Sparkles className="inline mr-1.5 h-4 w-4" /> Generate with AI
                            </button>
                            <button
                                type="button"
                                onClick={() => setDescriptionMode('manual')}
                                data-testid="description-mode-manual-btn"
                                className={`flex-1 rounded-xl border text-sm font-semibold py-2 px-3 transition-colors ${descriptionMode === 'manual' ? 'bg-[#8B5CF6] text-white border-[#8B5CF6]' : 'bg-white text-slate-600 border-purple-200/50 hover:border-[#8B5CF6]'}`}
                            >
                                Write your own
                            </button>
                        </div>

                        {descriptionMode === 'ai' && !formData.description && (
                            <div>
                                <p className="text-xs text-slate-400 mb-1.5" style={{ fontFamily: 'Barlow, sans-serif' }}>
                                    Say something like: "Someone who can manage our CRM, handle inbound leads, and build weekly reports" or "A creative thinker who can own our social media strategy end to end"
                                </p>
                                <Textarea
                                    value={formData.brief}
                                    onChange={(e) => setFormData(prev => ({ ...prev, brief: e.target.value }))}
                                    placeholder="Describe in a short sentence what you need..."
                                    rows={2}
                                    className="rounded-xl border-purple-200/50"
                                    data-testid="brief-input"
                                />
                                <Button
                                    type="button"
                                    onClick={handleGenerateDescription}
                                    disabled={generatingDesc || !formData.brief.trim() || !formData.title}
                                    className="mt-2 rounded-full bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-sm h-9 px-4"
                                    data-testid="generate-desc-btn"
                                >
                                    {generatingDesc ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating...</> : <><Sparkles className="mr-2 h-4 w-4" /> Generate Job Description</>}
                                </Button>
                                {!formData.title && (
                                    <p className="text-xs text-amber-600 mt-1.5" data-testid="generate-desc-title-hint">
                                        Select a job title above first — it's used to generate a relevant description.
                                    </p>
                                )}
                            </div>
                        )}

                        {(descriptionMode === 'manual' || formData.description) && (
                            <div>
                                <Textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                    placeholder="Write your own job description here..."
                                    rows={4}
                                    className="rounded-xl border-purple-200/50"
                                    data-testid="description-input"
                                />
                                <p className="text-xs text-slate-400 mt-1">You can edit this before posting</p>
                            </div>
                        )}
                    </div>

                    {/* Work Arrangement + Location */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <Label className="text-slate-700 font-semibold text-sm">Work Arrangement</Label>
                            <Select value={formData.work_arrangement} onValueChange={(val) => setFormData(prev => ({ ...prev, work_arrangement: val }))}>
                                <SelectTrigger className="mt-1.5 rounded-xl border-purple-200/50 bg-white" data-testid="arrangement-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-white rounded-xl">
                                    <SelectItem value="office">In the Office</SelectItem>
                                    <SelectItem value="hybrid">Hybrid</SelectItem>
                                    <SelectItem value="remote">Remote</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-slate-700 font-semibold text-sm">Location</Label>
                            <Select value={formData.location} onValueChange={(val) => setFormData(prev => ({ ...prev, location: val }))}>
                                <SelectTrigger className="mt-1.5 rounded-xl border-purple-200/50 bg-white" data-testid="location-select">
                                    <SelectValue placeholder="Select location" />
                                </SelectTrigger>
                                <SelectContent className="bg-white rounded-xl">
                                    <SelectItem value="London">London</SelectItem>
                                    <SelectItem value="Remote">Remote</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Salary */}
                    <div>
                        <Label className="text-slate-700 font-semibold text-sm">Annual Salary</Label>
                        <Input
                            value={formData.salary}
                            onChange={(e) => setFormData(prev => ({ ...prev, salary: e.target.value }))}
                            placeholder="e.g. £30,000 - £40,000"
                            className="mt-1.5 rounded-xl border-purple-200/50"
                            data-testid="salary-input"
                        />
                    </div>

                    {/* Skills */}
                    <div>
                        <Label className="text-slate-700 font-semibold text-sm">Required Skills</Label>
                        {selectedSkills.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2 mb-3">
                                {selectedSkills.map(skill => (
                                    <span key={skill} className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium text-white cursor-pointer transition-all hover:opacity-80" style={{ backgroundColor: '#9333EA' }} onClick={() => handleSkillToggle(skill)}>
                                        {skill} <X className="h-3 w-3 ml-1.5" />
                                    </span>
                                ))}
                            </div>
                        )}
                        <div className="flex flex-wrap gap-2 mt-2">
                            {COMMON_SKILLS.filter(s => !selectedSkills.includes(s)).slice(0, 12).map(skill => (
                                <button key={skill} type="button" onClick={() => handleSkillToggle(skill)} className="px-3 py-1.5 rounded-full text-sm border border-purple-200/50 hover:border-[#8B5CF6] hover:bg-purple-50 transition-colors text-slate-600 hover:text-[#8B5CF6]">
                                    + {skill}
                                </button>
                            ))}
                        </div>
                        <div className="flex gap-2 mt-3">
                            <Input
                                value={customSkill}
                                onChange={(e) => setCustomSkill(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddCustomSkill(); } }}
                                placeholder="Type a custom skill and press Enter"
                                className="rounded-xl border-purple-200/50 flex-1"
                                data-testid="custom-skill-input"
                            />
                            <Button type="button" onClick={handleAddCustomSkill} variant="outline" className="rounded-xl border-purple-200/50 hover:border-[#8B5CF6] hover:bg-purple-50" data-testid="add-skill-btn">
                                Add
                            </Button>
                        </div>
                    </div>

                    {/* Ranked Required Skills */}
                    {selectedSkills.length > 0 && (
                        <div>
                            <Label className="text-slate-700 font-semibold text-sm">Rank Your Top Skills by Importance</Label>
                            <p className="text-xs text-slate-400 mt-0.5 mb-3" style={{ fontFamily: 'Barlow, sans-serif' }}>
                                Select up to 5 critical skills and rate their importance (1 = nice to have, 5 = essential). This powers candidate alignment scoring.
                            </p>
                            {/* Add skills to ranking */}
                            <div className="flex flex-wrap gap-2 mb-3">
                                {selectedSkills.filter(s => !rankedSkills.find(r => r.skill === s)).map(skill => (
                                    <button key={skill} type="button" onClick={() => handleAddRankedSkill(skill)}
                                        disabled={rankedSkills.length >= 5}
                                        className="px-3 py-1.5 rounded-full text-xs border border-purple-200/50 hover:border-[#8B5CF6] hover:bg-purple-50 transition-colors text-slate-500 hover:text-[#8B5CF6] disabled:opacity-40 disabled:cursor-not-allowed"
                                        data-testid={`rank-skill-${skill}`}
                                    >
                                        + Rank "{skill}"
                                    </button>
                                ))}
                            </div>
                            {/* Ranked list */}
                            {rankedSkills.length > 0 && (
                                <div className="space-y-2">
                                    {rankedSkills.map((item, index) => (
                                        <div key={item.skill} className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3 border border-purple-100/50" data-testid={`ranked-skill-${index}`}>
                                            <div className="flex flex-col gap-0.5">
                                                <button type="button" onClick={() => moveRankedSkill(index, -1)} className="text-slate-400 hover:text-[#8B5CF6] disabled:opacity-30" disabled={index === 0}><ChevronUp className="h-3 w-3" /></button>
                                                <button type="button" onClick={() => moveRankedSkill(index, 1)} className="text-slate-400 hover:text-[#8B5CF6] disabled:opacity-30" disabled={index === rankedSkills.length - 1}><ChevronDown className="h-3 w-3" /></button>
                                            </div>
                                            <span className="text-sm font-medium text-slate-700 flex-1">{item.skill}</span>
                                            <div className="flex gap-1">
                                                {[1,2,3,4,5].map(n => (
                                                    <button key={n} type="button" onClick={() => handleRankChange(item.skill, n)}
                                                        className={`w-8 h-8 rounded-full text-xs font-bold transition-all ${
                                                            item.importance === n
                                                                ? 'text-white shadow-sm'
                                                                : 'border border-purple-200 text-slate-500 hover:border-[#8B5CF6]'
                                                        }`}
                                                        style={item.importance === n ? { background: 'linear-gradient(135deg, #9333EA, #7C3AED)' } : {}}
                                                        data-testid={`rank-${item.skill}-${n}`}
                                                    >
                                                        {n}
                                                    </button>
                                                ))}
                                            </div>
                                            <button type="button" onClick={() => handleRemoveRankedSkill(item.skill)} className="text-slate-400 hover:text-red-500 ml-1">
                                                <X className="h-4 w-4" />
                                            </button>
                                        </div>
                                    ))}
                                    <p className="text-xs text-slate-400" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                                        {rankedSkills.length}/5 skills ranked
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Working Style Preferences */}
                    <div className="space-y-5">
                        <div>
                            <Label className="text-slate-700 font-semibold text-sm">Your Team's Working Style</Label>
                            <p className="text-xs text-slate-400 mt-0.5 mb-3" style={{ fontFamily: 'Barlow, sans-serif' }}>
                                Select what best describes your team. This helps match candidates whose working style fits your environment.
                            </p>
                        </div>

                        <div>
                            <Label className="text-slate-600 text-xs font-medium mb-2 block">Environment</Label>
                            <div className="flex flex-wrap gap-2">
                                {ENVIRONMENT_OPTIONS.map(opt => (
                                    <button key={opt} type="button" onClick={() => toggleWorkingStyle('environment', opt)}
                                        className={`px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                                            workingStylePrefs.environment.includes(opt)
                                                ? 'text-white shadow-sm'
                                                : 'border border-purple-200/50 text-slate-600 hover:border-[#8B5CF6] hover:bg-purple-50'
                                        }`}
                                        style={workingStylePrefs.environment.includes(opt) ? { backgroundColor: '#9333EA' } : {}}
                                        data-testid={`ws-env-${opt.slice(0,10)}`}
                                    >
                                        {opt}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <Label className="text-slate-600 text-xs font-medium mb-2 block">Team Size</Label>
                            <div className="flex flex-wrap gap-2">
                                {TEAM_OPTIONS.map(opt => (
                                    <button key={opt} type="button" onClick={() => toggleWorkingStyle('team_size', opt)}
                                        className={`px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                                            workingStylePrefs.team_size.includes(opt)
                                                ? 'text-white shadow-sm'
                                                : 'border border-purple-200/50 text-slate-600 hover:border-[#14B8A6] hover:bg-teal-50'
                                        }`}
                                        style={workingStylePrefs.team_size.includes(opt) ? { backgroundColor: '#14B8A6' } : {}}
                                        data-testid={`ws-team-${opt.slice(0,10)}`}
                                    >
                                        {opt}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <Label className="text-slate-600 text-xs font-medium mb-2 block">Management Style</Label>
                            <div className="flex flex-wrap gap-2">
                                {MANAGER_OPTIONS.map(opt => (
                                    <button key={opt} type="button" onClick={() => toggleWorkingStyle('management_style', opt)}
                                        className={`px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                                            workingStylePrefs.management_style.includes(opt)
                                                ? 'text-white shadow-sm'
                                                : 'border border-purple-200/50 text-slate-600 hover:border-[#9333EA] hover:bg-purple-50'
                                        }`}
                                        style={workingStylePrefs.management_style.includes(opt) ? { backgroundColor: '#9333EA' } : {}}
                                        data-testid={`ws-mgmt-${opt.slice(0,10)}`}
                                    >
                                        {opt}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Working Style Tags (Role Alignment) */}
                    <div>
                        <Label className="text-slate-700 font-semibold text-sm">Role Characteristics</Label>
                        <p className="text-xs text-slate-400 mt-0.5 mb-3" style={{ fontFamily: 'Barlow, sans-serif' }}>
                            Select the tags that describe this role. These influence how candidates are scored and ranked for alignment.
                        </p>
                        <div className="flex flex-wrap gap-2" data-testid="working-style-tags">
                            {WORKING_STYLE_TAGS.map(tag => (
                                <button
                                    key={tag}
                                    type="button"
                                    onClick={() => toggleWorkingStyleTag(tag)}
                                    className={`px-3.5 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                                        selectedWorkingStyleTags.includes(tag)
                                            ? 'text-white shadow-sm'
                                            : 'border border-slate-200 text-slate-600 hover:border-[#10B981] hover:bg-emerald-50'
                                    }`}
                                    style={selectedWorkingStyleTags.includes(tag) ? { background: 'linear-gradient(135deg, #10B981, #059669)' } : {}}
                                    data-testid={`wst-${tag.toLowerCase().replace(/[\s/]+/g, '-')}`}
                                >
                                    {tag}
                                </button>
                            ))}
                        </div>
                        {selectedWorkingStyleTags.length > 0 && (
                            <p className="text-xs text-emerald-600 mt-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                                {selectedWorkingStyleTags.length} tag{selectedWorkingStyleTags.length !== 1 ? 's' : ''} selected
                            </p>
                        )}
                    </div>

                    {/* Trial Pay Rate */}
                    <div>
                        <Label className="text-slate-700 font-semibold text-sm">Trial Pay Rate</Label>
                        <p className="text-xs text-slate-400 mt-0.5 mb-2">UK minimum wage is currently £{MIN_WAGE}/hr</p>
                        <div className="grid grid-cols-2 gap-3">
                            <button
                                type="button"
                                onClick={() => setPayType('minimum')}
                                className={`rounded-full h-11 text-sm font-semibold transition-all duration-200 ${
                                    payType === 'minimum' ? 'text-white shadow-sm' : 'border border-purple-200/50 text-slate-600 hover:border-[#8B5CF6] hover:bg-purple-50'
                                }`}
                                style={payType === 'minimum' ? { background: 'linear-gradient(135deg, #9333EA, #7C3AED)' } : {}}
                                data-testid="min-wage-btn"
                            >
                                Minimum Wage (£{MIN_WAGE}/hr)
                            </button>
                            <button
                                type="button"
                                onClick={() => setPayType('custom')}
                                className={`rounded-full h-11 text-sm font-semibold transition-all duration-200 ${
                                    payType === 'custom' ? 'text-white shadow-sm' : 'border border-purple-200/50 text-slate-600 hover:border-[#8B5CF6] hover:bg-purple-50'
                                }`}
                                style={payType === 'custom' ? { background: 'linear-gradient(135deg, #9333EA, #7C3AED)' } : {}}
                                data-testid="custom-rate-btn"
                            >
                                Custom Rate
                            </button>
                        </div>
                        {payType === 'custom' && (
                            <div className="mt-3">
                                <Input
                                    type="number"
                                    step="0.01"
                                    min={MIN_WAGE}
                                    value={customPayRate}
                                    onChange={(e) => setCustomPayRate(e.target.value)}
                                    placeholder={`Min £${MIN_WAGE}/hr`}
                                    className="rounded-xl border-purple-200/50"
                                    data-testid="custom-rate-input"
                                />
                            </div>
                        )}
                    </div>

                    {/* Hours per Day (Shift Pattern) */}
                    <div>
                        <Label className="text-slate-700 font-semibold text-sm">Hours per Day</Label>
                        <div className="grid grid-cols-3 gap-3 mt-2">
                            {SHIFT_OPTIONS.map(opt => (
                                <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => setShiftType(opt.value)}
                                    className={`rounded-full h-11 text-sm font-semibold transition-all duration-200 ${
                                        shiftType === opt.value ? 'text-white shadow-sm' : 'border border-purple-200/50 text-slate-600 hover:border-[#8B5CF6] hover:bg-purple-50'
                                    }`}
                                    style={shiftType === opt.value ? { background: 'linear-gradient(135deg, #9333EA, #7C3AED)' } : {}}
                                    data-testid={`shift-${opt.value}-btn`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                        {shiftType === 'custom' && (
                            <div className="grid grid-cols-3 gap-3 mt-3">
                                <div>
                                    <Label className="text-xs text-slate-500">Start Time</Label>
                                    <Input value={customShiftFrom} onChange={(e) => setCustomShiftFrom(e.target.value)} placeholder="e.g. 8am" className="mt-1 rounded-xl border-purple-200/50" data-testid="custom-shift-from" />
                                </div>
                                <div>
                                    <Label className="text-xs text-slate-500">End Time</Label>
                                    <Input value={customShiftTo} onChange={(e) => setCustomShiftTo(e.target.value)} placeholder="e.g. 3pm" className="mt-1 rounded-xl border-purple-200/50" data-testid="custom-shift-to" />
                                </div>
                                <div>
                                    <Label className="text-xs text-slate-500">Total Hours</Label>
                                    <Input type="number" step="0.5" min="1" max="12" value={customShiftHours} onChange={(e) => setCustomShiftHours(e.target.value)} placeholder="7" className="mt-1 rounded-xl border-purple-200/50" data-testid="custom-shift-hours" />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Trial Length */}
                    <div>
                        <Label className="text-slate-700 font-semibold text-sm">Trial Length</Label>
                        <p className="text-xs text-slate-400 mt-0.5 mb-2">Choose between 1 and 10 days</p>
                        <div className="flex flex-wrap gap-2">
                            {[1,2,3,4,5,6,7,8,9,10].map(d => (
                                <button
                                    key={d}
                                    type="button"
                                    onClick={() => setFormData(prev => ({ ...prev, trial_length_days: String(d) }))}
                                    className={`w-11 h-11 rounded-full text-sm font-semibold transition-all duration-200 ${
                                        formData.trial_length_days === String(d) ? 'text-white shadow-sm' : 'border border-purple-200/50 text-slate-600 hover:border-[#8B5CF6] hover:bg-purple-50'
                                    }`}
                                    style={formData.trial_length_days === String(d) ? { background: 'linear-gradient(135deg, #9333EA, #7C3AED)' } : {}}
                                    data-testid={`trial-day-${d}`}
                                >
                                    {d}
                                </button>
                            ))}
                        </div>
                        <p className="text-xs text-slate-400 mt-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                            {formData.trial_length_days} day{formData.trial_length_days !== '1' ? 's' : ''} selected
                        </p>
                    </div>

                    {/* Cost Estimator */}
                    {costEstimate && (
                        <div className="rounded-2xl p-6 border border-purple-200/50" style={{ background: 'linear-gradient(135deg, #1E1B4B 0%, #312E81 50%, #4C1D95 100%)' }} data-testid="cost-estimate">
                            <span className="uppercase tracking-[0.15em] text-xs font-semibold text-purple-300 block mb-4" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                                Trial Cost Estimator
                            </span>
                            <div className="space-y-3 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-purple-200">Platform fee ({costEstimate.days} days @ £{costEstimate.platform_fee_per_day}/day)</span>
                                    <span className="font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>£{costEstimate.platform_fee_total.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-purple-200">Candidate pay ({costEstimate.days} days @ £{costEstimate.hourly_rate}/hr x {costEstimate.hours_per_day}hrs)</span>
                                    <span className="font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>£{costEstimate.candidate_pay_total.toFixed(2)}</span>
                                </div>
                                <div className="border-t border-purple-400/30 pt-3 flex justify-between">
                                    <span className="text-purple-200 font-semibold">Trial Total</span>
                                    <span className="font-bold text-white text-base" style={{ fontFamily: 'Unbounded, sans-serif' }}>£{costEstimate.trial_total.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-purple-200">Conversion fee (only if hired)</span>
                                    <span className="font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>£{(costEstimate.conversion_fee ?? costEstimate.hiring_fee ?? 750).toFixed(2)}</span>
                                </div>
                                <div className="border-t border-purple-400/30 pt-3 flex justify-between text-lg">
                                    <span className="text-purple-200 font-semibold">Total if Hired</span>
                                    <span className="font-bold text-[#34D399]" style={{ fontFamily: 'Unbounded, sans-serif' }}>£{costEstimate.total_if_hired.toFixed(2)}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    <Button type="submit" className="w-full btn-primary h-12" disabled={loading} data-testid="submit-btn">
                        {loading ? 'Posting Job...' : 'Post Job'}
                    </Button>
                </form>
            </main>
        </div>
    );
};

export default JobPost;
